import React, { useState, useEffect } from 'react';
import { auth } from '../firebase';
import { getMissedFasts, toggleFastStatus, deleteMissedFast } from '../firebase/collections';
import FastCard from './FastCard';
import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { CalendarIcon, BellIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

function FastsList() {
  const [fasts, setFasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMonths, setExpandedMonths] = useState({});

  useEffect(() => {
    const loadFasts = async () => {
      if (!auth.currentUser) {
        setFasts([]);
        setLoading(false);
        return;
      }

      try {
        const userFasts = await getMissedFasts(auth.currentUser.uid);
        setFasts(userFasts);
        
        // Initialize all months as expanded
        const months = {};
        userFasts.forEach(fast => {
          const monthKey = new Date(fast.date).toLocaleString('en-US', { month: 'long', year: 'numeric' });
          months[monthKey] = true;
        });
        setExpandedMonths(months);
      } catch (err) {
        console.error('Error loading fasts:', err);
        setError('Failed to load missed fasts. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadFasts();
  }, []);

  const handleComplete = async (fast) => {
    try {
      await toggleFastStatus(auth.currentUser.uid, fast.id);
      
      // Update local state
      setFasts(prevFasts =>
        prevFasts.map(f =>
          f.id === fast.id 
            ? { 
                ...f, 
                status: f.status === 'MISSED' ? 'MADE_UP' : 'MISSED',
                completedAt: f.status === 'MISSED' ? new Date().toISOString() : null
              } 
            : f
        )
      );
    } catch (err) {
      console.error('Error toggling fast status:', err);
      alert('Failed to update fast status. Please try again.');
    }
  };

  const handleDelete = async (fast) => {
    try {
      await deleteMissedFast(auth.currentUser.uid, fast.id);
      
      // Update local state
      setFasts(prevFasts => prevFasts.filter(f => f.id !== fast.id));
    } catch (err) {
      console.error('Error deleting fast:', err);
      throw err; // Let the FastCard component handle the error
    }
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths(prev => ({
      ...prev,
      [monthKey]: !prev[monthKey]
    }));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#F5B100]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Error Loading Fasts
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.includes('Database setup required') ? (
                <>
                  <p className="font-medium">Initial setup needed:</p>
                  <p className="mt-1">The database needs a one-time setup. Please contact the administrator to create the required index.</p>
                  <p className="mt-2 text-xs text-red-600">Technical details: {error}</p>
                </>
              ) : (
                error
              )}
            </div>
            <div className="mt-4">
              <button
                onClick={() => window.location.reload()}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (fasts.length === 0) {
    return (
      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-blue-50 rounded-lg">
                <CalendarIcon className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold">Track Your Progress</h3>
            </div>
            <p className="text-gray-600">
              Record your missed fasts and get personalized recommendations for makeup days, including Mondays, Thursdays, and the blessed White Days.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6">
            <div className="flex items-center space-x-3 mb-4">
              <div className="p-2 bg-purple-50 rounded-lg">
                <BellIcon className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold">Stay on Track</h3>
            </div>
            <p className="text-gray-600">
              Get gentle reminders about your makeup fasts and never miss completing them before the next Ramadan.
            </p>
          </div>
        </div>

        <div className="bg-blue-50 p-6 rounded-xl">
          <h4 className="font-medium text-blue-800 mb-3">Recommended Days for Makeup Fasts</h4>
          <ul className="space-y-2 text-blue-700">
            <li className="flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-blue-500" />
              <span>Mondays and Thursdays - following the Sunnah</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-blue-500" />
              <span>White Days (13th, 14th, 15th of lunar months) - extra rewards</span>
            </li>
            <li className="flex items-center space-x-2">
              <CheckCircleIcon className="h-5 w-5 text-blue-500" />
              <span>Days of Arafah and Ashura - highly recommended</span>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  // Group fasts by month
  const groupedFasts = fasts.reduce((groups, fast) => {
    const monthKey = new Date(fast.date).toLocaleString('en-US', { month: 'long', year: 'numeric' });
    if (!groups[monthKey]) {
      groups[monthKey] = [];
    }
    groups[monthKey].push(fast);
    return groups;
  }, {});

  return (
    <div className="space-y-6">
      {Object.entries(groupedFasts).map(([monthKey, monthFasts]) => (
        <div key={monthKey} className="bg-white rounded-lg shadow-sm overflow-hidden">
          <button
            onClick={() => toggleMonth(monthKey)}
            className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors duration-200"
          >
            <h3 className="text-lg font-semibold text-gray-900">{monthKey}</h3>
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-500">
                {monthFasts.length} {monthFasts.length === 1 ? 'fast' : 'fasts'}
              </span>
              {expandedMonths[monthKey] ? (
                <ChevronUpIcon className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              )}
            </div>
          </button>
          
          {expandedMonths[monthKey] && (
            <div className="p-4 space-y-4">
              {monthFasts.map(fast => (
                <FastCard
                  key={fast.id}
                  fast={fast}
                  onComplete={handleComplete}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      ))}

      {Object.keys(groupedFasts).length === 0 && (
        <div className="bg-blue-50 p-4 rounded-lg text-left">
          <h4 className="font-medium text-blue-800 mb-2">Did you know?</h4>
          <p className="text-blue-700 text-sm">
            The best days for voluntary fasting outside Ramadan are:
          </p>
          <ul className="text-sm text-blue-600 mt-2 space-y-1">
            <li>• Mondays and Thursdays</li>
            <li>• The White Days (13th, 14th, and 15th of each lunar month)</li>
            <li>• The Day of Arafah (9th of Dhul Hijjah)</li>
            <li>• The Day of Ashura (10th of Muharram)</li>
          </ul>
        </div>
      )}
    </div>
  );
}

export default FastsList; 